import { Link } from "react-router-dom";
import { ArrowRight, Lock, Sparkle, Stack, Lightning, Plugs } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen text-[var(--brand-ink)] noise">
      {/* Nav */}
      <header className="border-b-[1.5px] border-[var(--brand-ink)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-bold tracking-tight" data-testid="landing-brand">
            JobVault
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login"
              className="px-4 py-2 text-sm font-medium hover:underline">log in</Link>
            <Link to="/register" data-testid="nav-register"
              className="btn-hard bg-[var(--brand-ink)] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[var(--brand-ink)]">
              start free →
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero - Tetris Grid */}
      <section className="border-b-[1.5px] border-[var(--brand-ink)] grid-paper">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-7">
            <p className="label-tiny text-[var(--brand-muted)] mb-4">est. 2026 — the cv ends here</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[0.95] tracking-tight">
              enter cv.
              <br/>
              <span className="text-[var(--brand-primary)]">rewrite cv.</span>
              <br/>
              cry a little.
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--brand-muted)]">
              your job hunt&apos;s group chat. one tidy vault for your cv, certs, transcripts, and every
              form field that asks for the same thing twice. lock it once. use it forever.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-register"
                className="btn-hard bg-[var(--brand-primary)] text-white px-6 py-3.5 rounded-md font-medium flex items-center gap-2">
                build your vault <ArrowRight weight="bold" size={18}/>
              </Link>
              <Link to="/login" data-testid="hero-cta-login"
                className="btn-hard bg-white text-[var(--brand-ink)] px-6 py-3.5 rounded-md font-medium">
                i already have one
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 font-mono-d text-xs text-[var(--brand-muted)]">
              <span>· no recruiter spam</span>
              <span>· export anytime</span>
              <span>· extension-ready</span>
            </div>
          </div>

          {/* Right Bento — illustrative preview of what a real vault looks like */}
          <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-4 self-end relative">
            <div className="col-span-2 flex items-center justify-between mb-1">
              <p className="label-tiny text-[var(--brand-muted)]">/preview · sample vault</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full border-[1.5px] border-[var(--brand-ink)] bg-white font-mono-d text-[10px] tracking-[0.18em] uppercase">
                example — not a real user
              </span>
            </div>
            <div className="col-span-2 card-hard bento-card rounded-md p-6 bg-[var(--bolt-butter)] relative">
              <span aria-hidden="true" className="absolute right-4 top-4 inline-block w-3 h-3 rounded-full bg-[var(--bolt-terracotta)] border-[1.5px] border-[var(--brand-ink)]"/>
              <p className="label-tiny mb-2">/personal info</p>
              <p className="font-display text-2xl font-semibold">Ada L.</p>
              <p className="text-sm text-[var(--brand-muted)]">London · ada@vault.app</p>
            </div>
            <div className="card-hard bento-card rounded-md p-5 bg-[var(--pastel-mint)] relative">
              <span aria-hidden="true" className="absolute right-3 top-3 inline-block w-2.5 h-2.5 rounded-full bg-[var(--bolt-sage)] border-[1.5px] border-[var(--brand-ink)]"/>
              <p className="label-tiny mb-2">/work</p>
              <p className="font-display text-lg leading-tight font-semibold">SWE @ Acme</p>
              <p className="text-xs text-[var(--brand-muted)] mt-1">2023 — now</p>
            </div>
            <div className="card-hard bento-card rounded-md p-5 bg-[var(--bolt-blush)] relative">
              <span aria-hidden="true" className="absolute right-3 top-3 inline-block w-2.5 h-2.5 rounded-full bg-[var(--bolt-plum)] border-[1.5px] border-[var(--brand-ink)]"/>
              <p className="label-tiny mb-2">/edu</p>
              <p className="font-display text-lg leading-tight font-semibold">BSc CS</p>
              <p className="text-xs text-[var(--brand-muted)] mt-1">UCL · 2:1</p>
            </div>
            <div className="col-span-2 card-hard bento-card rounded-md p-5 bg-[var(--bolt-sky)] flex items-center justify-between relative">
              <span aria-hidden="true" className="absolute right-3 top-3 inline-block w-2.5 h-2.5 rounded-full bg-[var(--brand-primary)] border-[1.5px] border-[var(--brand-ink)]"/>
              <div>
                <p className="label-tiny mb-2">/references + skills</p>
                <p className="font-display text-lg font-semibold">tagged. tracked. ready.</p>
              </div>
              <div className="font-mono-d text-xs text-[var(--brand-muted)] text-right">
                copy any<br/>field instantly
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tagline — warm pinky gradient, the one you liked */}
      <section
        className="relative overflow-hidden border-t-[1.5px] border-b-[1.5px] border-[var(--brand-ink)]"
        style={{ background: "linear-gradient(135deg, var(--bolt-blush) 0%, #FFD9C2 45%, var(--bolt-sky) 100%)" }}
      >
        <div className="h-1.5 w-full" style={{ background: "var(--brand-primary)" }} aria-hidden="true"/>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <p className="font-display text-3xl md:text-5xl tracking-tight text-[var(--brand-ink)]">
            Lock it once. <span className="text-[var(--brand-primary)]">Use it forever.</span>
          </p>
        </div>
      </section>

      {/* Features grid */}
      <section className="border-b-[1.5px] border-[var(--brand-ink)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-0">
          {[
            { icon: Stack, t: "the vault", d: "every form field, in one tidy drawer. work, edu, apprenticeships — entered once, mapped everywhere." },
            { icon: Lock, t: "your docs, your rules", d: "certs, transcripts, ID. uploaded once, tagged, expiry-tracked, never lost in a downloads folder again." },
            { icon: Plugs, t: "vault mini (browser companion)", d: "your vault, one tab away while you browse jobs. peek at anything. copy into forms. tailored cvs + cover letters on the go. autofill still in beta." },
            { icon: Sparkle, t: "rewrite, smartly", d: "feed your messy bullets to claude. get back tight, achievement-coded copy. no buzzword soup." },
            { icon: Lightning, t: "track everything", d: "kanban board for applications. applied → interview → offer → rejected → therapy." },
            { icon: Stack, t: "export it all", d: "json export endpoint. take your data anywhere. yes, even to that other app you swear you'll switch to." },
          ].map((f, i) => {
            const Ic = f.icon;
            return (
              <div key={i} className={`p-8 ${i % 3 !== 2 ? "md:border-r-[1.5px]" : ""} ${i < 3 ? "md:border-b-[1.5px]" : ""} border-[var(--brand-ink)]`}>
                <Ic size={32} weight="duotone" />
                <h3 className="font-display text-2xl font-semibold mt-4 tracking-tight">{f.t}</h3>
                <p className="text-sm leading-relaxed text-[var(--brand-muted)] mt-3">{f.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative grid-paper border-b-[1.5px] border-[var(--brand-ink)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
              ready to stop retyping <span className="text-[var(--brand-primary)]">your address?</span>
            </h2>
            <p className="mt-4 text-[var(--brand-ink)]/70 text-lg">
              a vault for the chronically applying. takes 90 seconds. free, no card, no recruiter newsletters.
            </p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/register" data-testid="footer-cta-register"
              className="btn-hard bg-[var(--brand-primary)] text-white px-8 py-4 rounded-md inline-flex items-center gap-2 font-medium">
              claim your vault <ArrowRight weight="bold" size={18}/>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t-[1.5px] border-[var(--brand-ink)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between font-mono-d text-xs text-[var(--brand-muted)] flex-wrap gap-3">
          <span>JobVault — © 2026</span>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/privacy" className="hover:text-[var(--brand-ink)]" data-testid="footer-privacy">privacy</Link>
            <Link to="/terms" className="hover:text-[var(--brand-ink)]" data-testid="footer-terms">terms</Link>
            <Link to="/cookies" className="hover:text-[var(--brand-ink)]" data-testid="footer-cookies">cookies</Link>
            <span>built for the chronically applying</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
